<template>
  <div class="sales-dashboard min-h-screen bg-secondary-50">
    <!-- Header -->
    <SalesDashboardHeader 
      activeTab="budget" 
      :showFilters="false" 
      :showKpis="false" 
    />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <div class="flex flex-col items-center gap-3">
          <div class="w-8 h-8 border-3 border-securdia-blue border-t-transparent rounded-full animate-spin"></div>
          <span class="text-sm text-secondary-500">{{ $t('sales_dashboard.loading_budget') }}</span>
        </div>
      </div>

      <div v-else>
        <!-- Header Section -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-bold text-secondary-800">{{ $t('sales_dashboard.budget_title') }}</h2>
            <p class="text-secondary-500 mt-1">{{ $t('sales_dashboard.budget_description') }}</p>
          </div>
          
          <button 
            @click="showCreateModal = true"
            class="px-4 py-2 bg-securdia-blue text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 shadow-sm"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            {{ $t('sales_dashboard.create_new_goal') }}
          </button>
        </div>

        <!-- Filter Row -->
        <div class="bg-white rounded-lg shadow-sm p-4 mb-6 border border-secondary-100">
          <div class="flex flex-wrap items-center gap-4">
            <!-- Scope Filter -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-secondary-600">{{ $t('sales_dashboard.show_for') }}:</span>
              <div class="flex bg-secondary-100 rounded-lg p-0.5">
                <button 
                  @click="viewScope = 'company'"
                  :class="viewScope === 'company' 
                    ? 'bg-white text-securdia-blue shadow-sm' 
                    : 'text-secondary-600 hover:text-secondary-800'"
                  class="px-3 py-1.5 text-sm font-medium rounded-md transition-all"
                >
                  {{ $t('sales_dashboard.scope_company') }}
                </button>
                <button 
                  @click="viewScope = 'seller'"
                  :class="viewScope === 'seller' 
                    ? 'bg-white text-securdia-blue shadow-sm' 
                    : 'text-secondary-600 hover:text-secondary-800'"
                  class="px-3 py-1.5 text-sm font-medium rounded-md transition-all"
                >
                  {{ $t('sales_dashboard.scope_per_seller') }}
                </button>
              </div>
            </div>

            <!-- Seller Dropdown (if seller scope) -->
            <div v-if="viewScope === 'seller'" class="flex items-center gap-2">
              <select v-model="selectedSellerId" class="sd-select">
                <option value="">{{ $t('sales_dashboard.filter_all_sellers') }}</option>
                <option v-for="seller in sellers" :key="seller.id" :value="seller.id">
                  {{ seller.name }}
                </option>
              </select>
            </div>

            <div class="border-l border-secondary-200 h-6 mx-2"></div>

            <!-- Time Period -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-secondary-600">{{ $t('sales_dashboard.period_label') }}:</span>
              <div class="flex bg-secondary-100 rounded-lg p-0.5">
                <button 
                  @click="timePeriod = 'month'"
                  :class="timePeriod === 'month' 
                    ? 'bg-white text-securdia-blue shadow-sm' 
                    : 'text-secondary-600 hover:text-secondary-800'"
                  class="px-3 py-1.5 text-sm font-medium rounded-md transition-all"
                >
                  {{ $t('sales_dashboard.period_month') }}
                </button>
                <button 
                  @click="timePeriod = 'year'"
                  :class="timePeriod === 'year' 
                    ? 'bg-white text-securdia-blue shadow-sm' 
                    : 'text-secondary-600 hover:text-secondary-800'"
                  class="px-3 py-1.5 text-sm font-medium rounded-md transition-all"
                >
                  {{ $t('sales_dashboard.period_year') }}
                </button>
              </div>
            </div>

            <!-- Year/Month Selector -->
            <div class="flex gap-2">
              <select v-model="selectedYear" class="sd-select">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
              <select v-if="timePeriod === 'month'" v-model="selectedMonth" class="sd-select">
                <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
              </select>
            </div>

            <div class="border-l border-secondary-200 h-6 mx-2"></div>

            <!-- Goal Type Filter -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-secondary-600">{{ $t('sales_dashboard.goal_type_filter') }}:</span>
              <select v-model="goalTypeFilter" class="sd-select">
                <option value="">{{ $t('sales_dashboard.filter_all') }}</option>
                <option value="revenue">{{ $t('sales_dashboard.goal_type_revenue') }}</option>
                <option value="contracts">{{ $t('sales_dashboard.goal_type_contracts') }}</option>
                <option value="units">{{ $t('sales_dashboard.goal_type_units') }}</option>
                <option value="category">{{ $t('sales_dashboard.goal_type_category') }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="sd-kpi-card sd-kpi-card--primary sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.total_goals') }}</p>
            <p class="sd-kpi-card__value text-securdia-blue">{{ filteredGoals.length }}</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--success sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.achieved_goals') }}</p>
            <p class="sd-kpi-card__value text-green-600">{{ achievedGoalsCount }}</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--accent sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.on_track') }}</p>
            <p class="sd-kpi-card__value text-securdia-orange">{{ onTrackGoalsCount }}</p>
          </div>
          <div class="sd-kpi-card sd-kpi-card--neutral sd-kpi-card--compact">
            <p class="sd-kpi-card__title">{{ $t('sales_dashboard.avg_progress') }}</p>
            <p class="sd-kpi-card__value text-secondary-800">{{ Math.min(100, avgProgress).toFixed(0) }}%</p>
          </div>
        </div>

        <!-- Goals List -->
        <div class="space-y-4">
          <!-- Empty State -->
          <div v-if="filteredGoals.length === 0" class="bg-white rounded-lg shadow-sm p-12 text-center border border-secondary-100">
            <div class="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-secondary-700 mb-2">{{ $t('sales_dashboard.no_goals_created') }}</h3>
            <p class="text-secondary-500 mb-4">{{ $t('sales_dashboard.no_goals_description') }}</p>
            <button 
              @click="showCreateModal = true"
              class="px-4 py-2 bg-securdia-blue text-white rounded-lg hover:bg-blue-600"
            >
              {{ $t('sales_dashboard.create_goal_btn') }}
            </button>
          </div>

          <!-- Goal Cards -->
          <BudgetGoalCard 
            v-for="goal in filteredGoals" 
            :key="goal.id"
            :goal="goal"
            :contracts="relevantContracts(goal)"
            :sellers="sellers"
            @edit="editGoal"
            @delete="deleteGoal"
          />
        </div>
      </div>
    </div>

    <!-- Create/Edit Goal Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-secondary-100">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-bold text-secondary-800">
              {{ editingGoal ? $t('sales_dashboard.edit_goal') : $t('sales_dashboard.create_new_goal') }}
            </h3>
            <button @click="closeModal" class="text-secondary-400 hover:text-secondary-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6 space-y-4">
          <!-- Goal Name -->
          <div>
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.goal_name_label') }}</label>
            <input 
              v-model="goalForm.name"
              type="text"
              :placeholder="$t('sales_dashboard.goal_name_placeholder')"
              class="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-securdia-blue/20 focus:border-securdia-blue"
            >
          </div>

          <!-- Goal Type -->
          <div>
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.goal_type_label') }}</label>
            <select v-model="goalForm.type" class="sd-select w-full">
              <option value="revenue">{{ $t('sales_dashboard.goal_type_revenue_kr') }}</option>
              <option value="contracts">{{ $t('sales_dashboard.goal_type_contracts') }}</option>
              <option value="units">{{ $t('sales_dashboard.goal_type_units_sold') }}</option>
              <option value="category">{{ $t('sales_dashboard.goal_type_category') }}</option>
            </select>
          </div>

          <!-- Category (if category type) -->
          <div v-if="goalForm.type === 'category'">
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.product_category') }}</label>
            <select v-model="goalForm.category" class="sd-select w-full">
              <option value="alarm">{{ $t('sales_dashboard.cat_personal_alarm') }}</option>
              <option value="ajax">{{ $t('sales_dashboard.cat_perimeter') }}</option>
              <option value="tag">{{ $t('sales_dashboard.cat_tags') }}</option>
              <option value="display">{{ $t('sales_dashboard.cat_display') }}</option>
              <option value="vehicle_tracker">{{ $t('sales_dashboard.cat_vehicle_tracker') }}</option>
              <option value="software">{{ $t('sales_dashboard.cat_software') }}</option>
              <option value="simcard">{{ $t('sales_dashboard.cat_simcard') }}</option>
              <option value="accessory">{{ $t('sales_dashboard.cat_accessory') }}</option>
            </select>
          </div>

          <!-- Target Value -->
          <div>
            <label class="block text-sm font-medium text-secondary-700 mb-1">
              {{ $t('sales_dashboard.target_value_label') }}
              <span class="text-secondary-400 font-normal">
                ({{ goalForm.type === 'revenue' ? 'kr' : $t('sales_dashboard.unit_pcs') }})
              </span>
            </label>
            <input 
              v-model.number="goalForm.target"
              type="number"
              min="0"
              placeholder="0"
              class="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-securdia-blue/20 focus:border-securdia-blue"
            >
            
            <!-- Current Progress Preview -->
            <div v-if="goalForm.year" class="mt-3 p-3 bg-secondary-50 rounded-lg border border-secondary-200">
              <div class="flex items-center justify-between text-sm mb-2">
                <span class="text-secondary-600">{{ $t('sales_dashboard.current_achieved') }}:</span>
                <span class="font-semibold text-secondary-800">
                  {{ goalForm.type === 'revenue' ? formatCurrency(currentFormProgress.current) : currentFormProgress.current }}
                  {{ goalForm.type !== 'revenue' ? $t('sales_dashboard.unit_pcs') : '' }}
                </span>
              </div>
              <div class="w-full bg-secondary-200 rounded-full h-2">
                <div 
                  class="h-2 rounded-full transition-all duration-300"
                  :class="currentFormProgress.progress >= 100 ? 'bg-green-500' : currentFormProgress.progress >= 50 ? 'bg-securdia-blue' : 'bg-securdia-orange'"
                  :style="{ width: `${currentFormProgress.progress}%` }"
                ></div>
              </div>
              <div class="flex justify-between text-xs mt-1">
                <span class="text-secondary-500">{{ currentFormProgress.progress.toFixed(0) }}% {{ $t('sales_dashboard.of_target') }}</span>
                <span v-if="goalForm.target > 0" class="text-secondary-500">
                  {{ $t('sales_dashboard.remaining') }}: 
                  {{ goalForm.type === 'revenue' 
                    ? formatCurrency(Math.max(0, goalForm.target - currentFormProgress.current))
                    : Math.max(0, goalForm.target - currentFormProgress.current) + ' ' + $t('sales_dashboard.unit_pcs')
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Scope -->
          <div>
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.applies_to') }}</label>
            <select v-model="goalForm.scope" class="sd-select w-full">
              <option value="company">{{ $t('sales_dashboard.scope_whole_company') }}</option>
              <option value="seller">{{ $t('sales_dashboard.scope_specific_seller') }}</option>
            </select>
          </div>

          <!-- Seller (if seller scope) -->
          <div v-if="goalForm.scope === 'seller'">
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.seller_label') }}</label>
            <select v-model="goalForm.sellerId" class="sd-select w-full">
              <option value="">{{ $t('sales_dashboard.select_seller') }}</option>
              <option v-for="seller in sellers" :key="seller.id" :value="seller.id">
                {{ seller.name }}
              </option>
            </select>
            <p v-if="sellers.length === 0" class="text-xs text-amber-600 mt-1">
              {{ $t('sales_dashboard.no_sellers_warning') }}
            </p>
          </div>

          <!-- Time Period -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.period_label') }}</label>
              <select v-model="goalForm.period" class="sd-select w-full">
                <option value="month">{{ $t('sales_dashboard.period_month') }}</option>
                <option value="quarter">{{ $t('sales_dashboard.period_quarter') }}</option>
                <option value="year">{{ $t('sales_dashboard.period_year') }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.year_label') }}</label>
              <select v-model="goalForm.year" class="sd-select w-full">
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>

          <!-- Month/Quarter (if applicable) -->
          <div v-if="goalForm.period === 'month'">
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.month_label') }}</label>
            <select v-model="goalForm.month" class="sd-select w-full">
              <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
            </select>
          </div>
          <div v-if="goalForm.period === 'quarter'">
            <label class="block text-sm font-medium text-secondary-700 mb-1">{{ $t('sales_dashboard.quarter_label') }}</label>
            <select v-model="goalForm.quarter" class="sd-select w-full">
              <option :value="1">Q1 ({{ $t('months.Jan') }}-{{ $t('months.Mar') }})</option>
              <option :value="2">Q2 ({{ $t('months.Apr') }}-{{ $t('months.Jun') }})</option>
              <option :value="3">Q3 ({{ $t('months.Jul') }}-{{ $t('months.Sep') }})</option>
              <option :value="4">Q4 ({{ $t('months.Oct') }}-{{ $t('months.Dec') }})</option>
            </select>
          </div>
        </div>

        <div class="p-6 border-t border-secondary-100 flex justify-end gap-3">
          <button 
            @click="closeModal"
            class="px-4 py-2 text-secondary-600 hover:text-secondary-800"
          >
            {{ $t('sales_dashboard.cancel_btn') }}
          </button>
          <button 
            @click="saveGoal"
            :disabled="!isGoalFormValid"
            class="px-4 py-2 bg-securdia-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ editingGoal ? $t('sales_dashboard.save_changes') : $t('sales_dashboard.create_goal_btn') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div class="p-6">
          <div class="flex items-center gap-4 mb-4">
            <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-secondary-800">{{ $t('sales_dashboard.delete_goal_title') }}</h3>
              <p class="text-secondary-500">{{ $t('sales_dashboard.delete_cannot_undo') }}</p>
            </div>
          </div>
          <p class="text-secondary-600 mb-6">
            {{ $t('sales_dashboard.delete_confirm_message', { name: goalToDelete?.name }) }}
          </p>
          <div class="flex justify-end gap-3">
            <button 
              @click="cancelDelete"
              class="px-4 py-2 text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              {{ $t('sales_dashboard.cancel_btn') }}
            </button>
            <button 
              @click="confirmDelete"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {{ $t('sales_dashboard.delete_btn') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import axios from 'axios';
import SalesDashboardHeader from '@/components/sales/SalesDashboardHeader.vue';
import BudgetGoalCard from '@/components/sales/BudgetGoalCard.vue';

export default {
  name: 'SalesBudget',
  title() {
    return this.$t('page_titles.budget_goals');
  },
  components: {
    SalesDashboardHeader,
    BudgetGoalCard
  },
  data() {
    return {
      isLoading: true,
      viewScope: 'company',
      selectedSellerId: '',
      timePeriod: 'month',
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      goalTypeFilter: '',
      showCreateModal: false,
      showDeleteModal: false,
      goalToDelete: null,
      editingGoal: null,
      goals: [],
      budgetContracts: [],
      goalForm: {
        name: '',
        type: 'revenue',
        category: 'alarm',
        target: 0,
        scope: 'company',
        sellerId: '',
        period: 'month',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quarter: Math.ceil((new Date().getMonth() + 1) / 3)
      },
      months: [
        'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
        'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
      ]
    };
  },
  computed: {
    // Sellers getter with fallback to empty array
    sellers() {
      return this.$store.getters['salesDashboard/sellers'] || [];
    },
    
    availableYears() {
      const currentYear = new Date().getFullYear();
      return [currentYear - 1, currentYear, currentYear + 1];
    },

    filteredGoals() {
      return this.goals.filter(goal => {
        // Scope filter
        if (this.viewScope === 'company' && goal.scope !== 'company') return false;
        if (this.viewScope === 'seller') {
          if (goal.scope !== 'seller') return false;
          if (this.selectedSellerId && goal.sellerId !== this.selectedSellerId) return false;
        }

        // Time period filter
        if (goal.year !== this.selectedYear) return false;
        if (this.timePeriod === 'month' && goal.period === 'month' && goal.month !== this.selectedMonth) return false;

        // Goal type filter
        if (this.goalTypeFilter && goal.type !== this.goalTypeFilter) return false;

        return true;
      });
    },

    achievedGoalsCount() {
      return this.filteredGoals.filter(goal => this.calculateProgress(goal) >= 100).length;
    },

    onTrackGoalsCount() {
      return this.filteredGoals.filter(goal => {
        const progress = this.calculateProgress(goal);
        return progress >= 50 && progress < 100;
      }).length;
    },

    avgProgress() {
      if (this.filteredGoals.length === 0) return 0;
      const total = this.filteredGoals.reduce((sum, goal) => sum + this.calculateProgress(goal), 0);
      return total / this.filteredGoals.length;
    },

    isGoalFormValid() {
      // Name is required
      if (!this.goalForm.name || !this.goalForm.name.trim()) return false;
      
      // Target must be a positive number
      const target = Number(this.goalForm.target);
      if (isNaN(target) || target <= 0) return false;
      
      // If seller scope, sellerId is required
      if (this.goalForm.scope === 'seller' && !this.goalForm.sellerId) return false;
      
      // Period-specific validation
      if (this.goalForm.period === 'quarter' && !this.goalForm.quarter) return false;
      if (this.goalForm.period === 'month' && !this.goalForm.month) return false;
      
      return true;
    },

    // Calculate current progress for the goal being created/edited
    currentFormProgress() {
      const goal = this.goalForm;
      if (!goal.year) return { current: 0, progress: 0 };
      
      const contracts = this.relevantContracts(goal);
      let current = 0;

      switch (goal.type) {
        case 'revenue':
          current = contracts.reduce((sum, c) => {
            const monthly = c.monthlyValue || c.rental_month_cost || 0;
            const cash = c.cashValue || c.cash_sum || 0;
            return sum + monthly + cash;
          }, 0);
          break;
        
        case 'contracts':
          current = contracts.length;
          break;
        
        case 'units':
          current = contracts.reduce((sum, c) => {
            if (c.productCount) return sum + c.productCount;
            return sum + (c.products || []).reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
          break;
        
        case 'category':
          current = contracts.reduce((sum, c) => {
            return sum + (c.products || []).filter(p => p.category === goal.category)
              .reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
          break;
      }

      const target = Number(goal.target) || 0;
      const progress = target > 0 ? Math.min(100, (current / target) * 100) : 0;

      return { current, progress };
    }
  },
  async created() {
    await this.fetchData();
    await this.loadGoals();
  },
  methods: {
    ...mapActions('salesDashboard', ['fetchSellers']),

    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    async fetchData() {
      this.isLoading = true;
      try {
        // Fetch contracts for a wide date range to cover all potential goals
        // Get contracts from 2 years ago to 1 year in future
        const currentYear = new Date().getFullYear();
        const startDate = `${currentYear - 1}-01-01`;
        const endDate = `${currentYear + 1}-12-31`;
        
        await Promise.all([
          this.fetchContractsForBudget(startDate, endDate),
          this.fetchSellers()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchContractsForBudget(startDate, endDate) {
      // Fetch contracts directly without Vuex filters for budget calculations
      // Only active/extended contracts are included (same as KPI calculations)
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          limit: 2000
        });
        const response = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/contracts?${params}`
        );
        this.budgetContracts = response.data.data || [];
      } catch (error) {
        console.error('Error fetching contracts for budget:', error);
        this.budgetContracts = [];
      }
    },

    async loadGoals() {
      // Load goals from API
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/sales/budget-goals`
        );
        if (response.data.success) {
          // Map _id to id for frontend compatibility
          this.goals = (response.data.data || []).map(goal => ({
            ...goal,
            id: goal._id || goal.id
          }));
        }
      } catch (error) {
        console.error('Error loading goals:', error);
        this.goals = [];
      }
    },

    relevantContracts(goal) {
      // Use budgetContracts which are fetched with a wider date range
      const filtered = this.budgetContracts.filter(contract => {
        // Use startDate (from API) or fall back to other date fields
        const date = new Date(contract.startDate || contract.contract_start || contract.createdAt || contract.start_date);
        if (isNaN(date)) return false;
        
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const quarter = Math.ceil(month / 3);

        // Year filter
        if (year !== goal.year) return false;

        // Period filter
        if (goal.period === 'month' && month !== goal.month) return false;
        if (goal.period === 'quarter' && quarter !== goal.quarter) return false;

        // Seller filter - use sellerId from API
        if (goal.scope === 'seller' && goal.sellerId) {
          const contractSellerId = contract.sellerId || contract.seller_id || contract.salesperson_id || contract.user_id;
          if (contractSellerId !== goal.sellerId) return false;
        }

        return true;
      });
      
      return filtered;
    },

    calculateProgress(goal) {
      const contracts = this.relevantContracts(goal);
      let current = 0;

      switch (goal.type) {
        case 'revenue':
          current = contracts.reduce((sum, c) => {
            // Use monthlyValue + cashValue from API (same as BudgetGoalCard)
            const monthly = c.monthlyValue || c.rental_month_cost || 0;
            const cash = c.cashValue || c.cash_sum || 0;
            return sum + monthly + cash;
          }, 0);
          break;
        
        case 'contracts':
          current = contracts.length;
          break;
        
        case 'units':
          current = contracts.reduce((sum, c) => {
            // Use productCount from API
            if (c.productCount) return sum + c.productCount;
            return sum + (c.products || []).reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
          break;
        
        case 'category':
          current = contracts.reduce((sum, c) => {
            return sum + (c.products || []).filter(p => p.category === goal.category)
              .reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
          break;
      }

      if (goal.target <= 0) return 0;
      return (current / goal.target) * 100;
    },

    editGoal(goal) {
      this.editingGoal = goal;
      this.goalForm = { ...goal };
      this.showCreateModal = true;
    },

    deleteGoal(goal) {
      this.goalToDelete = goal;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (this.goalToDelete) {
        try {
          const goalId = this.goalToDelete._id || this.goalToDelete.id;
          await axios.delete(
            `${process.env.VUE_APP_SERVER_URL}/sales/budget-goals/${goalId}`
          );
          this.goals = this.goals.filter(g => (g._id || g.id) !== goalId);
          this.$toast?.success(this.$t('sales_dashboard.goal_deleted_success'));
        } catch (error) {
          console.error('Error deleting goal:', error);
          this.$toast?.error(this.$t('sales_dashboard.goal_delete_error'));
        }
      }
      this.showDeleteModal = false;
      this.goalToDelete = null;
    },

    cancelDelete() {
      this.showDeleteModal = false;
      this.goalToDelete = null;
    },

    async saveGoal() {
      if (!this.isGoalFormValid) return;

      // Get seller name if applicable
      let sellerName = '';
      if (this.goalForm.scope === 'seller' && this.goalForm.sellerId) {
        const seller = this.sellers.find(s => s.id === this.goalForm.sellerId);
        sellerName = seller?.name || '';
      }

      const goalData = {
        ...this.goalForm,
        sellerName
      };

      try {
        if (this.editingGoal) {
          // Update existing via API
          const goalId = this.editingGoal._id || this.editingGoal.id;
          const response = await axios.put(
            `${process.env.VUE_APP_SERVER_URL}/sales/budget-goals/${goalId}`,
            goalData
          );
          if (response.data.success) {
            const index = this.goals.findIndex(g => (g._id || g.id) === goalId);
            if (index !== -1) {
              this.goals[index] = { 
                ...response.data.data, 
                id: response.data.data._id 
              };
            }
            this.$toast?.success(this.$t('sales_dashboard.goal_updated_success'));
          }
        } else {
          // Create new via API
          const response = await axios.post(
            `${process.env.VUE_APP_SERVER_URL}/sales/budget-goals`,
            goalData
          );
          if (response.data.success) {
            this.goals.push({
              ...response.data.data,
              id: response.data.data._id
            });
            this.$toast?.success(this.$t('sales_dashboard.goal_created_success'));
          }
        }
      } catch (error) {
        console.error('Error saving goal:', error);
        this.$toast?.error(this.$t('sales_dashboard.goal_save_error'));
      }

      this.closeModal();
    },

    closeModal() {
      this.showCreateModal = false;
      this.editingGoal = null;
      this.goalForm = {
        name: '',
        type: 'revenue',
        category: 'alarm',
        target: 0,
        scope: 'company',
        sellerId: '',
        period: 'month',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quarter: Math.ceil((new Date().getMonth() + 1) / 3)
      };
    }
  }
};
</script>

<style scoped>
.sd-select {
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}

.sd-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 91, 165, 0.2);
  border-color: #005ba5;
}
</style>
